import { Link } from 'react-router-dom';
import { Plus, Minus, ShoppingCart, CreditCard } from 'lucide-react';
import { Button } from './ui/button';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { formatCurrency } from '../lib/api';
import { useState } from 'react';
import { toast } from 'sonner';

// WhatsApp Icon SVG
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const ProductCard = ({ product, index = 0 }) => {
  const { addToCart, cart, updateQuantity } = useCart();
  const { isAuthenticated } = useAuth();
  const { getPaymentMethodsText } = useSettings();
  const [isAdding, setIsAdding] = useState(false);
  
  const cartItem = cart.items.find(item => item.product_id === product.id);
  const quantity = cartItem?.quantity || 0;
  
  const discount = product.original_price 
    ? Math.round((1 - product.price / product.original_price) * 100)
    : 0;

  const paymentText = getPaymentMethodsText();

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setIsAdding(true);
    await addToCart(product.id, 1, product);
    setIsAdding(false);
  };

  const handleUpdateQuantity = async (e, newQuantity) => {
    e.preventDefault();
    e.stopPropagation();
    await updateQuantity(product.id, newQuantity);
  };

  const handleShare = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const url = `${window.location.origin}/produto/${product.id}`;
    const text = `🛒 Confira *${product.name}* por apenas *${formatCurrency(product.price)}*!\n\n`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text + url)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <Link 
      to={`/produto/${product.id}`}
      className="product-card block bg-white rounded-2xl border border-slate-100 overflow-hidden animate-slide-up"
      style={{ animationDelay: `${index * 0.1}s` }}
      data-testid={`product-card-${product.id}`}
    >
      {/* Image */}
      <div className="relative aspect-square overflow-hidden bg-slate-100">
        <img
          src={product.images?.[0] || 'https://images.unsplash.com/photo-1612708074992-c7936e82c99b?w=600'}
          alt={product.name}
          className="product-image w-full h-full object-cover"
          loading="lazy"
        />
        
        {/* Discount Badge */}
        {discount > 0 && (
          <div className="discount-badge" data-testid="discount-badge">
            -{discount}%
          </div>
        )}
        
        {/* Kit Badge */}
        {product.is_kit && (
          <div className="absolute top-3 right-3 bg-primary text-white px-2 py-1 rounded-full text-xs font-bold">
            KIT
          </div>
        )}
        
        {/* WhatsApp Share Button */}
        <button
          onClick={handleShare}
          className="absolute bottom-3 right-3 w-9 h-9 bg-green-500 hover:bg-green-600 rounded-full flex items-center justify-center transition-colors shadow-lg"
          data-testid={`share-button-${product.id}`}
          title="Compartilhar no WhatsApp"
        >
          <WhatsAppIcon className="w-5 h-5 text-white" />
        </button>
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="font-heading font-semibold text-foreground line-clamp-2 mb-1">
          {product.name}
        </h3>
        
        {product.description && (
          <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
            {product.description}
          </p>
        )}

        {/* Price */}
        <div className="flex items-baseline gap-2 mb-2">
          <span className="price-current" data-testid={`price-${product.id}`}>
            {formatCurrency(product.price)}
          </span>
          {product.original_price && (
            <span className="price-original">
              {formatCurrency(product.original_price)}
            </span>
          )}
        </div>

        {/* Payment Methods */}
        {paymentText && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-3">
            <CreditCard className="w-3.5 h-3.5" />
            <span>{paymentText}</span>
          </div>
        )}

        {/* Add to Cart / Quantity Controls */}
        {quantity > 0 ? (
          <div className="flex items-center justify-between" data-testid={`quantity-controls-${product.id}`}>
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9 rounded-full"
              onClick={(e) => handleUpdateQuantity(e, quantity - 1)}
              data-testid={`decrease-quantity-${product.id}`}
            >
              <Minus className="h-4 w-4" />
            </Button>
            <span className="font-bold text-lg">{quantity}</span>
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9 rounded-full"
              onClick={(e) => handleUpdateQuantity(e, quantity + 1)}
              data-testid={`increase-quantity-${product.id}`}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <Button
            className="w-full rounded-full shadow-primary"
            onClick={handleAddToCart}
            disabled={isAdding || product.stock === 0}
            data-testid={`add-to-cart-${product.id}`}
          >
            {isAdding ? (
              <span className="animate-pulse">Adicionando...</span>
            ) : product.stock === 0 ? (
              'Esgotado'
            ) : (
              <>
                <ShoppingCart className="mr-2 h-4 w-4" />
                Adicionar
              </>
            )}
          </Button>
        )}
      </div>
    </Link>
  );
};

export default ProductCard;
